class MultiProductSubscriptions extends HTMLElement {
  constructor() {
    super();
    this.multiProductSubscriptionData = JSON.parse(
      document.getElementById("multi-product-subscription-data").textContent
    );

    this.cart = document.querySelector("cart-drawer");
    this.flushDialog = document.querySelector("#how-many-flush-dialog");

    // GWP (Gift With Purchase) selection state
    this.selectedGwpVariantId = null;
    this.selectedGwpTitle = null;
  }

  // Called when the element is inserted into a document.
  connectedCallback() {
    this.section = this;

    // Cache important selectors once
    this._cacheSelectors();

    // Guard clause: make sure required elements exist
    if (!this.subscriptionSelectors || !this.addToCartBtn) {
      console.warn("Missing required elements for MultiProductSubscriptions.");
      return;
    }

    // Parse any variants/subscription types from the URL
    const variantObj = this.getVariantFromURLParam();
    const subscriptionType = this.getSubscriptionFromURLParam();

    // Preselect subscription option if present in URL
    if (subscriptionType) {
      this._selectSubscriptionButton(subscriptionType);
    }

    // Preselect product options if variantObj is present
    if (variantObj) {
      this._preselectOptions(variantObj.handle);
      this.loadModule(variantObj.handle);
      this._filterGalleryByActiveOption();
    } else {
      this.loadModule();
    }

    // Set up all event listeners in one place
    this._initEventListeners();

    // Dispatch a custom event so other scripts can react
    this.dispatchEvent(
      new CustomEvent("MultiProductSubscriptions:connected", {
        bubbles: true,
        cancelable: true,
      })
    );

    // Set up the optional flush dialog trigger
    this._initHowManyFlushDialog();
  }

  // Helper to cache all the main elements we need
  _cacheSelectors() {
    this.variantId = document.querySelector("#variant-id");
    this.sellingPlan = document.querySelector("#selling-plan");
    this.variantSelectors = this.section.querySelector("#variant-selectors");
    this.productOptionSelector = this.section.querySelector(
      "#product-option-selector"
    );
    this.subscriptionSelectors = this.section.querySelector(
      "#subscription-selectors"
    );
    this.addToCartBtn = this.section.querySelector(
      "#multi-product-subscriptions--atc"
    );
    this.openHowManyFlushDialogBtn = this.querySelector("#how-many-flush");
  }

  // Preselect subscription plan button by URL param
  _selectSubscriptionButton(subscriptionType) {
    // Unselect all first
    this.subscriptionSelectors
      .querySelectorAll("[data-subscription-selector-selected]")
      .forEach((el) =>
        el.setAttribute("data-subscription-selector-selected", "false")
      );

    // Select the one matching subscriptionType
    const subBtn = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="${subscriptionType}"]`
    );
    if (subBtn) {
      subBtn.setAttribute("data-subscription-selector-selected", "true");
      subBtn.setAttribute("aria-pressed", "true");
    }
  }

  // Preselect product option buttons by handle
  _preselectOptions(handle) {
    if (!this.productOptionSelector) return;
    const selectorsContainers = this.productOptionSelector.querySelectorAll(
      ".selectors-container"
    );

    selectorsContainers.forEach((container) => {
      const buttons = container.querySelectorAll("button[data-option-handle]");
      buttons.forEach((btn) => {
        const optionHandle = btn.dataset.optionHandle;
        // Use substring match to allow multi-word/multi-part options
        if (handle.includes(optionHandle)) {
          btn.setAttribute("data-variant-selected", "true");
          btn.setAttribute("aria-pressed", "true");
        } else {
          btn.setAttribute("data-variant-selected", "false");
          btn.setAttribute("aria-pressed", "false");
        }
      });
    });
  }

  // Filter media gallery by currently selected product option, if present
  _filterGalleryByActiveOption() {
    if (!this.productOptionSelector) return;
    const activeProductOptionSelector =
      this.productOptionSelector.querySelector(
        `.selectors-container button[data-variant-selected="true"]`
      );
    if (activeProductOptionSelector) {
      this.filterMediaGalleryByVariant(
        activeProductOptionSelector.dataset.optionHandle
      );
    }
  }

  // Group all event listeners here for easy scaling and clean-up
  _initEventListeners() {
    if (this.variantSelectors) {
      this.variantSelectors.addEventListener(
        "click",
        this.variantSelectorsEventHandler.bind(this)
      );
    }

    if (this.productOptionSelector) {
      this.productOptionSelector.addEventListener(
        "click",
        this.productOptionSelectorEventHandler.bind(this)
      );
    }

    this.subscriptionSelectors.addEventListener(
      "click",
      this.subscriptionSelectorsEventHandler.bind(this)
    );

    this.addToCartBtn.addEventListener("click", this.addToCart.bind(this));

    // GWP event listeners (store bound handlers for cleanup)
    this._boundGwpSelectedHandler = (e) => {
      this.selectedGwpVariantId = e.detail.variantId;
      this.selectedGwpTitle = e.detail.title;
    };
    this._boundGwpDeselectedHandler = () => {
      this.selectedGwpVariantId = null;
      this.selectedGwpTitle = null;
    };

    document.addEventListener("gwp:selected", this._boundGwpSelectedHandler);
    document.addEventListener("gwp:deselected", this._boundGwpDeselectedHandler);
  }

  // Set up the "How many flush" dialog button if present
  _initHowManyFlushDialog() {
    if (this.openHowManyFlushDialogBtn && this.flushDialog) {
      this.openHowManyFlushDialogBtn.addEventListener("click", () => {
        this.flushDialog.show();
      });
    }
  }

  // Called when the element is removed from a document.
  disconnectedCallback() {
    if (this.productOptionSelector) {
      this.productOptionSelector.removeEventListener(
        "click",
        this.productOptionSelectorEventHandler.bind(this)
      );
    }

    if (this.variantSelectors) {
      this.variantSelectors.removeEventListener(
        "click",
        this.variantSelectorsEventHandler.bind(this)
      );
    }

    if (this.subscriptionSelectors) {
      this.subscriptionSelectors.removeEventListener(
        "click",
        this.subscriptionSelectorsEventHandler.bind(this)
      );
    }

    // Clean up GWP event listeners
    document.removeEventListener("gwp:selected", this._boundGwpSelectedHandler);
    document.removeEventListener("gwp:deselected", this._boundGwpDeselectedHandler);
  }

  /**
   * Handles click events on variant selectors.
   * @param {Event} event - The click event.
   */
  variantSelectorsEventHandler(event) {
    const currentSelectedBtn = event.target.closest("button");
    if (!currentSelectedBtn) return;
    const { handle } = currentSelectedBtn.dataset;
    const previousSelectedBtn = this.variantSelectors.querySelector(
      `[data-variant-selected="true"]`
    );
    previousSelectedBtn.dataset.variantSelected = false;
    currentSelectedBtn.dataset.variantSelected = true;

    this.loadModule(handle);
  }

  productOptionSelectorEventHandler(event) {
    const currentSelectedBtn = event.target.closest("button");
    if (!currentSelectedBtn) return;

    const selectorsContainer = currentSelectedBtn.closest(
      ".selectors-container"
    );
    const previousSelectedBtn = selectorsContainer.querySelector(
      `[data-variant-selected="true"]`
    );
    previousSelectedBtn.dataset.variantSelected = false;
    currentSelectedBtn.dataset.variantSelected = true;

    this.loadModule();
    const { optionHandle } = currentSelectedBtn.dataset;
    this.filterMediaGalleryByVariant(optionHandle);
  }

  /**
   * Handles click events on subscription selectors.
   * @param {Event} event - The click event.
   */
  subscriptionSelectorsEventHandler(event) {
    const currentSelectedSubscription = event.target.closest(
      ".subscription-selector"
    );
    if (!currentSelectedSubscription) return false;
    const previousSelectedSubscription =
      this.subscriptionSelectors.querySelector(
        `[data-subscription-selector-selected="true"]`
      );
    previousSelectedSubscription.dataset.subscriptionSelectorSelected = false;
    currentSelectedSubscription.dataset.subscriptionSelectorSelected = true;

    this.updateSubscriptionTagContent(currentSelectedSubscription);

    this.loadModule();
  }

  /**
   * Toggles the visibility of media carousels based on the selected variant.
   * @param {string} handle - The handle of the variant selector.
   */
  filterMediaGalleryByVariant(handle) {
    const mediaGalleryElm = document.querySelector(".mediaGallery-container");
    const altTextList = mediaGalleryElm?.getAttribute("data-alt-text-list");
    if (mediaGalleryElm && altTextList.indexOf(handle) > -1) {
      mediaGalleryElm.setAttribute("data-active-variant-handle", handle);
    }
  }

  /**
   * Loads the module with the specified handle.
   * @param {string} handle - The handle of the variant selector.
   */
  loadModule(handle = "") {
    const currentSelectedVariantHandle =
      handle || this.getSelectedVariantSelectorHandle();

    const productVariant = this.getProductVariantByHandle(
      currentSelectedVariantHandle
    );

    const variantSku = productVariant.sku;

    const multiProductVariant = this.getMultiProductVariantByHandleOrSku(
      currentSelectedVariantHandle,
      variantSku
    );

    const subscriptionSelected = this.getSelectedSubscription();

    this.updateVariantAndSellingPlanId(
      subscriptionSelected,
      productVariant,
      multiProductVariant
    );

    this.updateSubscriptionSelectorsPrices(
      productVariant,
      multiProductVariant,
      subscriptionSelected
    );
  }

  /**
   * Updates the variant ID and selling plan ID based on subscription selection.
   * @param {string} subscriptionSelected - The selected subscription type.
   * @param {Object} productVariant - The product variant object.
   * @param {Object} multiProductVariant - The multi-product variant object.
   */
  updateVariantAndSellingPlanId(
    subscriptionSelected,
    productVariant,
    multiProductVariant
  ) {
    let variantIdToSet = null;
    switch (subscriptionSelected) {
      case "multi-product": {
        this.variantId.value = multiProductVariant.id;
        this.sellingPlan.value = this.getMultiProductSellingPlanId();
        variantIdToSet = multiProductVariant.id;
        break;
      }
      case "default-selling-plan": {
        this.variantId.value = productVariant.id;
        this.sellingPlan.value = this.getProductSellingPlanId();
        variantIdToSet = productVariant.id;
        break;
      }
      case "one-time-purchase": {
        this.variantId.value = productVariant.id;
        this.sellingPlan.value = "";
        variantIdToSet = productVariant.id;
        break;
      }
      default:
        break;
    }
    if (variantIdToSet) {
      const subscriptionType = this.getSelectedSubscription();
      this.updateURLWithVariantAndSubscription(
        variantIdToSet,
        subscriptionType
      );
    }
  }

  /**
   * Updates the prices displayed on subscription selectors.
   * @param {Object} productVariant - The product variant object.
   * @param {Object} multiProductVariant - The multi-product variant object.
   * @param {String} subscriptionSelected - Currently selected subscription
   */
  updateSubscriptionSelectorsPrices(
    productVariant,
    multiProductVariant,
    subscriptionSelected
  ) {
    const {
      sellingPlanPrice: multiProductVariantSellingPlanPrice,
      price: multiProductVariantPrice,
      id: multiProductVariantId,
    } = multiProductVariant;

    const productDefaultSellingPlan = this.getDefaultSellingPlan();
    const productRecurrenceAndFrequency =
      this.calculateRecurrencePatternAndFrequency(
        productDefaultSellingPlan.frequencyNumber,
        productDefaultSellingPlan.frequencyPeriod
      );

    const multiProductSellingPlan = this.getMultiProductSellingPlan();
    const multiProductRecurrenceAndFrequency =
      this.calculateRecurrencePatternAndFrequency(
        multiProductSellingPlan.frequencyNumber,
        multiProductSellingPlan.frequencyPeriod
      );

    const {
      sellingPlanPrice: productVariantSellingPlanPrice,
      price: productVariantPrice,
      comparePrice: productVariantComparePrice,
      id: productVariantId,
    } = productVariant;

    this.setSubscriptionSelectorPrices(
      multiProductVariantSellingPlanPrice,
      multiProductVariantPrice,
      multiProductRecurrenceAndFrequency,
      multiProductVariantId,
      productVariantPrice,
      productVariantComparePrice
    );

    this.setDefaultSellingPlanPrices(
      productVariantSellingPlanPrice,
      productVariantPrice,
      productRecurrenceAndFrequency,
      productVariantId,
      productVariantComparePrice
    );

    this.setProductVariantPrice(
      productVariantPrice,
      productVariantComparePrice,
      productVariantId
    );

    let event = new CustomEvent("multi-product-subscription::update-prices", {
      detail: {
        subscriptionSelected,
        productVariant,
        productRecurrenceAndFrequency,
        multiProductVariant,
        multiProductRecurrenceAndFrequency,
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Updates the content of the subscription tag with the savings percentage.
   * If no savings percentage or subscription tag is found, hides all subscription tags.
   *
   * Note: Hiding all subscription tags is a temporary solution
   * until the Intelligems test is completed. Ideally, there should
   * be only one element of this type.
   *
   * @param {HTMLElement} currentSelectedSubscription Subscription selector
   */
  updateSubscriptionTagContent(currentSelectedSubscription) {
    const savingsPercentage =
      currentSelectedSubscription.querySelector(".savings-pill")?.dataset
        .percentage;
    const subscriptionTag = this.querySelector(".subscription-tag");

    if (!savingsPercentage || savingsPercentage === "0") {
      subscriptionTag.style.display = "none";
      return;
    }
    subscriptionTag.style.display = "block";
    subscriptionTag.innerHTML = `Save ${savingsPercentage}%`;
  }

  /**
   * Adds the selected product to the cart.
   */
  async addToCart() {
    const formData = this.buildFormDataForRequest();

    try {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.innerHTML = "ADDING TO THE CART";

      const request = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const response = await request.json();

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "product-form",
        productVariantId: this.getVariantId(),
        cartData: response,
      });

      // Dispatch GWP claimed event if a GWP was included
      if (this.selectedGwpVariantId) {
        document.dispatchEvent(new CustomEvent("gwp:claimed"));
        this.selectedGwpVariantId = null;
        this.selectedGwpTitle = null;
      }

      this.cart.renderContents(response);
    } catch (error) {
      console.log(`❌ Multi Product Subscription | addToCart | ${error}`);
      alert(error);
      if (typeof hj === "function") {
        hj("event", "error_occurred");
      }
    } finally {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = "ADD TO CART";
      if (this.cart && this.cart.classList.contains("is-empty"))
        this.cart.classList.remove("is-empty");
    }
  }

  /**
   * Retrieves the selected subscription type.
   * @returns {string} The selected subscription type.
   */
  getSelectedSubscription() {
    return this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-selected="true"]`
    ).dataset.subscriptionSelectorType;
  }

  /**
   * Retrieves the handle of the selected variant selector.
   * @returns {string} The handle of the selected variant selector.
   */
  getSelectedVariantSelectorHandle() {
    if (this.variantSelectors) {
      return this.variantSelectors.querySelector(
        `[data-variant-selected="true"]`
      ).dataset.optionHandle;
    }

    if (this.productOptionSelector) {
      return Array.from(
        this.productOptionSelector.querySelectorAll(
          `[data-variant-selected="true"]`
        )
      )
        .map((el) => el.dataset.optionHandle)
        .join("-");
    }
  }

  /**
   * Retrieves the product variant by handle.
   * @param {string} handle - The handle of the product variant.
   * @returns {Object} The product variant object.
   */
  getProductVariantByHandle(handle) {
    return this.multiProductSubscriptionData.product.variants.find(
      (el) => el.handle === handle
    );
  }

  /**
   * Retrieves the multi-product variant by handle, or if there's no handle match it tries to compare the SKU of the normal product with the SKU of the 3 month supply using the suffix "-90".
   * @param {string} handle - The handle of the multi-product variant.
   * @param {string} sku - The sku of the multi-product variant.
   * @returns {Object} The multi-product variant object.
   */
  getMultiProductVariantByHandleOrSku(handle, sku) {
    return this.multiProductSubscriptionData.multiProductSubscription.variants.find(
      (el) => el.handle === handle || el.sku === sku + "-90"
    );
  }

  /**
   * Retrieve the product's selling plan object
   * @returns {object} Selling plan object
   */
  getDefaultSellingPlan() {
    return this.multiProductSubscriptionData.product.sellingPlan;
  }

  /**
   * Retrieve the multi product's selling plan object
   * @returns {object} Selling plan object
   */
  getMultiProductSellingPlan() {
    return this.multiProductSubscriptionData.multiProductSubscription
      .sellingPlan;
  }

  /**
   * Retrieves the selling plan ID for multi-product subscription.
   * @returns {number} The selling plan ID for multi-product subscription.
   */
  getMultiProductSellingPlanId() {
    return this.multiProductSubscriptionData.multiProductSubscription
      .sellingPlan.id;
  }

  /**
   * Retrieves the selling plan ID for the default product.
   * @returns {number} The selling plan ID for the default product.
   */
  getProductSellingPlanId() {
    return this.multiProductSubscriptionData.product.sellingPlan.id;
  }

  /**
   * Retrieves the variant ID.
   * @returns {number} The variant ID.
   */
  getVariantId() {
    return +this.variantId.value;
  }

  /**
   * Retrieves the selling plan ID.
   * @returns {number} The selling plan ID.
   */
  getSellingPlanId() {
    return +this.sellingPlan.value;
  }

  /**
   * Sets the prices for subscription selectors for multi-product subscription.
   * @param {number} multiProductVariantSellingPlanPrice - The selling plan price for multi-product variant.
   * @param {number} multiProductVariantPrice - The price for multi-product variant.
   * @param {Object} multiProductRecurrenceAndFrequency Selling plan object
   * @param {number} productVariantPrice - The price for product variant.
   */
  setSubscriptionSelectorPrices(
    multiProductVariantSellingPlanPrice,
    multiProductVariantPrice,
    multiProductRecurrenceAndFrequency,
    multiProductVariantId,
    productVariantPrice,
    productVariantComparePrice
  ) {
    const { pattern, months } = multiProductRecurrenceAndFrequency;
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="multi-product"]`
    );

    // const priceByFrequency = (
    //   multiProductVariantSellingPlanPrice / months
    // ).toFixed(2);
    const priceByFrequency = this.addZeroToDecimal(
      multiProductVariantSellingPlanPrice / months
    );

    selector.querySelector(
      ".selling-plan-price"
    ).innerHTML = `$${priceByFrequency}${months && " /mo"}`;

    const formattedPrice = this.addZeroToDecimal(
      multiProductVariantSellingPlanPrice
    );

    selector.querySelector(
      ".default-price"
    ).innerHTML = `$<span>${formattedPrice} paid ${pattern}`;

    // Use variant compare price of one-time purchase product, if empty then use one-time purchase price
    selector.querySelector(".selling-plan-compare-price").innerHTML = `$${
      productVariantComparePrice !== 0
        ? productVariantComparePrice
        : productVariantPrice
    }`;
  }

  /**
   * Sets the prices for subscription selectors for default product.
   * @param {number} productVariantSellingPlanPrice - The selling plan price for product variant.
   * @param {number} productVariantPrice - The price for product variant.
   * @param {Object} productRecurrenceAndFrequency Selling plan object
   */
  setDefaultSellingPlanPrices(
    productVariantSellingPlanPrice,
    productVariantPrice,
    productRecurrenceAndFrequency,
    productVariantId,
    productVariantComparePrice
  ) {
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="default-selling-plan"]`
    );
    const { pattern, months } = productRecurrenceAndFrequency;

    const fixedProductVariantSellingPlanPrice = this.addZeroToDecimal(
      productVariantSellingPlanPrice
    );

    selector.querySelector(
      ".selling-plan-price"
    ).innerHTML = `$${fixedProductVariantSellingPlanPrice} ${months && "/mo"}`;

    selector
      .querySelector(".selling-plan-price")
      .setAttribute("data-variant-id", productVariantId);

    selector.querySelector(
      ".default-price"
    ).innerHTML = `$<span data-variant-id="${productVariantId}">${fixedProductVariantSellingPlanPrice}</span> paid ${pattern}`;

    // If both the subscription and one-time price are the same, hide the compare price
    if (
      fixedProductVariantSellingPlanPrice === productVariantPrice.toString()
    ) {
      selector.querySelector(".selling-plan-compare-price").innerHTML = "";
      return;
    }
    // Use variant compare price of one-time purchase product, if empty then use one-time purchase price
    selector.querySelector(".selling-plan-compare-price").innerHTML = `$${
      productVariantComparePrice !== 0
        ? productVariantComparePrice
        : productVariantPrice
    }`;
  }

  /**
   * Sets the price for product variant in one-time purchase subscription.
   * @param {number} price - The price for product variant.
   * @param {number} comparePrice - The compare price for product variant.
   */
  setProductVariantPrice(price, comparePrice, productVariantId) {
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="one-time-purchase"]`
    );
    selector.querySelector(".selling-plan-price").innerHTML = `$${price}`;

    selector
      .querySelector(".selling-plan-price")
      .setAttribute("data-variant-id", productVariantId);
    // Get compare price from product variant, if empty then set empty inner html
    selector.querySelector(".selling-plan-compare-price").innerHTML =
      comparePrice == 0 ? "" : `$${comparePrice}`;
  }

  /**
   * Calculates the recurrence pattern based on the frequency number and period.
   * @param {number} recurrenceCount - The number of times the event repeats.
   * @param {string} recurrenceUnit - The unit of recurrence, either "week" or "day".
   * @returns {Object} The recurrence pattern.
   */
  calculateRecurrencePatternAndFrequency(frequencyNumber, frequencyPeriod) {
    const weeksInMonth = 4;
    const averageDaysInAMonth = 30.437;
    let pattern = "";
    let months = 0;
    let weeks = 0;

    const determineMonthlyPattern = (months) => {
      if (months === 1) return "monthly";
      if (months === 3) return "quarterly";
      return months > 1 ? `every ${months} months` : "monthly";
    };

    const determineWeeklyPattern = (weeks) => {
      return weeks > 1 ? `every ${frequencyNumber} weeks}` : "weekly";
    };

    if (frequencyPeriod === "week") {
      if (frequencyNumber < weeksInMonth) {
        pattern = determineWeeklyPattern(frequencyNumber);
        weeks = frequencyNumber;
        return { pattern, months, weeks };
      }

      months = frequencyNumber / weeksInMonth;
      pattern = determineMonthlyPattern(months);
      return { pattern, months, weeks };
    }

    if (frequencyPeriod === "day") {
      if (frequencyNumber < averageDaysInAMonth) {
        pattern = `monthly`;
        months = 1;
        return { pattern, months, weeks };
      }

      months = Math.round(frequencyNumber / averageDaysInAMonth);
      pattern = determineMonthlyPattern(months);
      return { pattern, months, weeks };
    }
  }

  addZeroToDecimal(number) {
    // const formattedPrice = Number(number).toLocaleString("en-US", {
    //   minimumFractionDigits: 2,
    //   maximumFractionDigits: 2,
    // });
    // return formattedPrice;

    if (Number.isInteger(number)) {
      return number.toFixed(0); // No decimal places for whole numbers
    } else {
      return number.toFixed(2); // Two decimal places for non-integers
    }
  }

  /**
   * Generates a unique bundle ID for linking main product with GWP.
   * @returns {string} A unique bundle ID
   */
  generateBundleId() {
    return `gwp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  buildFormDataForRequest() {
    // Generate bundle ID if GWP is selected (links main product with gift)
    const bundleId = this.selectedGwpVariantId ? this.generateBundleId() : null;

    // Build items array - main product first
    const items = [
      {
        id: this.getVariantId(),
        quantity: 1,
        ...(this.getSellingPlanId() && {
          selling_plan: this.getSellingPlanId(),
        }),
        ...(bundleId && {
          properties: {
            _gwp_bundle_id: bundleId,
          },
        }),
      },
    ];

    // Add GWP as separate line item if selected
    if (this.selectedGwpVariantId) {
      items.push({
        id: this.selectedGwpVariantId,
        quantity: 1,
        properties: {
          _gwp_uqora: "true",
          _gwp_bundle_id: bundleId,
        },
      });
    }

    return {
      items,
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }

  /**
   * Updates the URL to reflect the given variant ID and subscription type.
   * @param {number|string} variantId
   * @param {string} subscriptionType
   */
  updateURLWithVariantAndSubscription(variantId, subscriptionType) {
    const url = new URL(window.location);
    url.searchParams.set("variant", variantId);
    url.searchParams.set("subscription", subscriptionType);
    window.history.replaceState({}, "", url); // Changed from pushState to replaceState
  }

  /**
   * Gets the variant object from the URL 'variant' param, or null if not found/invalid.
   * @returns {Object|null} The variant object or null.
   */
  getVariantFromURLParam() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get("variant");
    if (variantParam) {
      const variantId = parseInt(variantParam, 10);

      // If the product has only one variant then return the normal default variant
      if (this.multiProductSubscriptionData.product.variants.length === 1) {
        return this.multiProductSubscriptionData.product.variants[0];
      }

      // Search in product variants
      const productVariant =
        this.multiProductSubscriptionData.product.variants.find(
          (v) => v.id === variantId
        );
      if (productVariant) return productVariant;

      // Search in multiProductSubscription variants
      const multiProductVariant =
        this.multiProductSubscriptionData.multiProductSubscription.variants.find(
          (v) => v.id === variantId
        );
      if (multiProductVariant) return multiProductVariant;

      return null;
    }
    return null;
  }
  /**
   * Gets the subscription type from the URL param, or null if not present.
   * @returns {string|null}
   */
  getSubscriptionFromURLParam() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("subscription");
  }
}
customElements.define("multi-product-subscriptions", MultiProductSubscriptions);
